import { useState } from "react";

import { cx } from "@/lib/cx";
import {
  addDays,
  compareDateKeys,
  formatDateKey,
  fromDateKey,
  relativeDayLabel,
  type DateKey,
} from "@/lib/dates";
import { segmentedItem, segmentedShell, type SegmentedSize } from "./Segmented";
import { Button } from "./Button";
import { Calendar } from "./overlay/DatePicker";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@/ui/icons";
import { Popover } from "./overlay/Popover";

/**
 * Moving between days — **one control**, not three.
 *
 * Tasks used to carry a segmented arrow pair, a conditional "Today" button *and*
 * a raw `<input type="date">` beside them (UI-REVIEW P14): three affordances for
 * one decision, one of them wearing the browser's own chrome in the middle of an
 * otherwise designed interface. Workout had no day navigation at all (P13).
 *
 * Now it is a single tray — ‹ , the day itself as a button that opens a
 * calendar, › — plus a separate Today text action that exists only when it
 * would do something.
 * Both day-bucketed surfaces use *this*; two day navigators that look slightly
 * different is precisely the drift the component layer exists to stop.
 *
 * Two shape decisions, both deliberate:
 *
 * - It is **not** a {@link Segmented}. These are three *actions*: pressing › is
 *   not "selecting next day" the way a range picker selects `30d`, and claiming
 *   radiogroup semantics would announce "‹" as an unselected radio option.
 *   `segmentedShell` / `segmentedItem` give the look without the lie.
 * - The body is {@link Calendar} rather than `DatePicker`, because the day
 *   button *is* the trigger — wrapping a picker that brings its own trigger
 *   would nest two poppers to render one popover.
 */

export type DayNavigatorProps = {
  value: DateKey;
  /** Today, so the label can read "Today" / "Yesterday" instead of a date. */
  today: DateKey;
  onChange: (next: DateKey) => void;
  /** Latest day that can be reached. Omit for no limit. */
  max?: DateKey;
  size?: SegmentedSize;
  className?: string;
};

export function DayNavigator({
  value,
  today,
  onChange,
  max,
  size = "sm",
  className,
}: DayNavigatorProps) {
  const [open, setOpen] = useState(false);

  const previous = addDays(value, -1);
  const next = addDays(value, 1);
  const atMax = max !== undefined && compareDateKeys(value, max) >= 0;

  return (
    <div className={cx("flex flex-wrap items-center gap-2", className)}>
      <div className={segmentedShell()}>
        <Button
          variant="tonal-ghost"
          size="fit"
          aria-label={`Previous day, ${formatDateKey(previous, "medium")}`}
          title={formatDateKey(previous, "weekday-short")}
          onClick={() => onChange(previous)}
          className={segmentedItem({ size, className: "px-2 pointer-coarse:min-w-tap" })}
        >
          <ChevronLeftIcon className="size-4" />
        </Button>

        <Popover
          open={open}
          onOpenChange={setOpen}
          align="center"
          padded={false}
          label="Choose a day"
          trigger={
            <Button
              variant="tonal-ghost"
              size="fit"
              // The label already says which day; the hint says what pressing it
              // does, which is the part a date button never makes obvious.
              aria-label={`${relativeDayLabel(value, today, "medium")} — choose a date`}
              className={segmentedItem({
                size,
                // A fixed floor so stepping from "Today" to "12 Aug 2026" does not
                // resize the tray under the cursor mid-click. The label is the
                // one thing in this tray that is a *value* rather than an action,
                // so it carries `text-ink` while the arrows stay `text-muted`.
                className: "min-w-[8.5rem] gap-2 px-2 text-ink",
              })}
            >
              <CalendarIcon className="size-4 shrink-0 text-faint" />
              <span className="truncate">{relativeDayLabel(value, today, "medium")}</span>
            </Button>
          }
        >
          <Calendar
            selected={fromDateKey(value)}
            today={today}
            max={max}
            onSelect={(picked) => {
              if (picked) {
                onChange(picked);
              }
              setOpen(false);
            }}
          />
        </Popover>

        <Button
          variant="tonal-ghost"
          size="fit"
          aria-label={`Next day, ${formatDateKey(next, "medium")}`}
          title={formatDateKey(next, "weekday-short")}
          onClick={() => onChange(next)}
          disabled={atMax}
          className={segmentedItem({ size, className: "px-2 pointer-coarse:min-w-tap" })}
        >
          <ChevronRightIcon className="size-4" />
        </Button>
      </div>
      {value === today ? null : (
        <Button variant="plain" size={size} onClick={() => onChange(today)}>
          Today
        </Button>
      )}
    </div>
  );
}
